import * as React from 'react';
import {
    Layer,
    TetherBehavior,
    TETHER_PLACEMENT,
} from 'baseui/layer';
import { useStyletron } from 'baseui';
import { tooltipBaseStyle } from '../Tooltip/styles';
import { TooltipProps } from '../Tooltip';

function Wrapper(props: any) {
    const [css] = useStyletron();
    const { offset, children, forwardedRef } = props;
    return (
        <div
            className={css({
                position: 'absolute',
                top: `${offset.top}px` || 0,
                left: `${offset.left}px` || 0,
                boxShadow: '0 1px 2px rgba(94, 124, 143, 0.4)',
                '@media print': {
                    visibility: 'hidden',
                },
                maxWidth: '400px',
                minWidth: '42px',
                ...tooltipBaseStyle({}, props),
                marginLeft: ["left", "right", "auto", "rightBottom", "rightTop", "leftBottom", "leftTop"].includes(props.placement) ? '8px' : '0',
                marginRight: ["left", "right", "auto", "rightBottom", "rightTop", "leftBottom", "leftTop"].includes(props.placement) ? '8px' : '0',
                marginTop: ["bottom", "top", "auto", "bottomRight", "topLeft", "topRight", "bottomLeft"].includes(props.placement) ? '8px' : '0',
                marginBottom: ["bottom", "top", "auto", "bottomRight", "topLeft", "topRight", "bottomLeft"].includes(props.placement) ? '8px' : '0',
            })}
            ref={forwardedRef}
        >
            {children}
        </div>
    );
}

const initialState = {
    isFirstOpen: false,
    isFirstMounted: false,
    offset1: { top: 0, left: 0 },
    arrow: { top: 0, left: 0 },
};

export class LayerTooltip extends React.Component<
    {
        content?: React.ReactNode
        size?: "default" | "large" | "compact" | "mini",
        placement?: "bottom" | "left" | "right" | "top" | "auto" | "bottomRight" | "rightBottom" | "topLeft" | "topRight" | "rightTop" | "bottomLeft" | "leftBottom" | "leftTop",
        mouseTooltipDelay?: number,
        mouseTooltipLifetime?: number,
        triggerType?: "click" | "hover",
    },
    typeof initialState
> {
    anchorRef1: any = React.createRef();
    arrowRef1: any = React.createRef();
    innerRef1: any = React.createRef();
    popperRef1: any = React.createRef();

    state = initialState;

    fromPopperPlacement = (placement: string): any | null => {
        const popoverPlacement: string = placement
            .replace(/(top|bottom)-start$/, '$1Left')
            .replace(/(top|bottom)-end$/, '$1Right')
            .replace(/(left|right)-start$/, '$1Top')
            .replace(/(left|right)-end$/, '$1Bottom');
        return popoverPlacement || null;
    }

    onPopperUpdate: any = (order: string, normalizedOffsets: any, data: any) => {
        const _placement = this.fromPopperPlacement(data.placement) || 'top';

        let newPos = normalizedOffsets.arrow
        
        /** TODO: TOP and RIGHT need mediaQueries */

        if (_placement?.includes('bottom')) {
            newPos = { 
                width: "16px",
                height: "16px",
                borderWidth: "8px",
                borderStyle: "solid",
                borderColor: "transparent",
                borderBottomColor: "var(--epods--color-bg-overlays)",
                transform: "none",
                background: "transparent",
                top: newPos.top - 16,
                left: newPos.left,
            }
        }

        if (_placement?.includes('top')) {
            newPos = { 
                width: "16px",
                height: "16px",
                borderWidth: "8px",
                borderStyle: "solid",
                borderColor: "transparent",
                borderTopColor: "var(--epods--color-bg-overlays)",
                transform: "none",
                background: "transparent",
                top: newPos.top  + this.innerRef1.current.offsetHeight + 6,
                left: newPos.left,
            }
        }

        if (_placement?.includes('right')) {
            newPos = { 
                width: "16px",
                height: "16px",
                borderWidth: "8px",
                borderStyle: "solid",
                borderColor: "transparent",
                borderRightColor: "var(--epods--color-bg-overlays)",
                transform: "none",
                background: "transparent",
                top: newPos.top,
                left: newPos.left - 16,
            }
        }

        if (_placement?.includes('left')) {
            newPos = { 
                width: "16px",
                height: "16px",
                borderWidth: "8px",
                borderStyle: "solid",
                borderColor: "transparent",
                borderLeftColor: "var(--epods--color-bg-overlays)",
                transform: "none",
                background: "transparent",
                top: newPos.top,
                left: newPos.left + this.innerRef1.current.offsetWidth + 14,
            }
        }

        this.setState({
            'arrow': newPos,
            [`offset${order}`]: normalizedOffsets.popper,
        } as any);
    };

    timeoutId: any = null;
    timeoutId2: any = null;

    render() {
        return (
            <React.Fragment>
                {this?.props?.children &&
                    React.cloneElement(this?.props?.children as React.ReactElement<any>, {
                        onMouseEnter: () => {
                            this.timeoutId = setTimeout(() => {
                                this.timeoutId2 && clearTimeout(this.timeoutId2);
                                this.setState({ isFirstOpen: this.props.triggerType === "hover" })
                                this.timeoutId2 = setTimeout(() => {
                                    this.setState({ isFirstOpen: false })
                                    this.timeoutId && clearTimeout(this.timeoutId);
                                }, this.props.mouseTooltipLifetime || 5000)
                            }, this.props.mouseTooltipDelay || 400)
                        },
                        onMouseLeave: () => {
                            this.setState({ isFirstOpen: false })
                            this.timeoutId && clearTimeout(this.timeoutId);
                        },
                        onFocus: () => this.setState({ isFirstOpen: this.props.triggerType === "hover" }),
                        onBlur: () => this.setState({ isFirstOpen: false }),
                        // TODO: Review the below code
                        // onClick: () => {
                        //     this.setState({ isFirstOpen: this.props.triggerType === "click" })

                        //     let subChildren: any = this.props.children;
                        //     if (typeof subChildren === 'object') {
                        //         // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                        //         subChildren?.props?.onClick && subChildren?.props?.onClick()
                        //     }

                        // },
                        ref: this.anchorRef1
                    })}
                {this.state.isFirstOpen ? (
                    <Layer
                        onMount={() => this.setState({ isFirstMounted: true })}
                        onUnmount={() => this.setState({ isFirstMounted: false })}
                    >
                        <TetherBehavior
                            anchorRef={this.anchorRef1.current}
                            arrowRef={this.arrowRef1.current}
                            popperRef={this.popperRef1.current}
                            onPopperUpdate={(...args) => {
                                this.onPopperUpdate(1, ...args)
                            }}
                            placement={this.props.placement || "auto"}
                        >
                            <Wrapper
                                forwardedRef={this.popperRef1}
                                offset={this.state.offset1}
                                color="rgba(0, 190, 255, 0.86)"
                                placement={this.props.placement || "auto"}
                                triggerType={this.props.triggerType || "hover"}
                            >
                                <div
                                    ref={this.arrowRef1}
                                    style={{
                                        position: 'absolute', width: '20px', height: '20px', background: 'var(--epods--color-bg-overlays)',
                                        transform: 'rotate(45deg)', zIndex: 0, ...this.state.arrow
                                    }}></div>
                                <div
                                    ref={this.innerRef1} data-epo="epo-layertooltip-inner"
                                    style={{
                                        zIndex: 1,
                                        position: 'relative',
                                        fontFamily: 'var(--epods--font-base)',
                                        color: 'var(--epods--color-text-inverted)'
                                    }}>
                                    {this.props.content}
                                </div>
                            </Wrapper>
                        </TetherBehavior>
                    </Layer>
                ) : null}
            </React.Fragment>
        );
    }
}


export interface withTooltipProps {
    tooltip?: TooltipProps;
}

export const withLayerTooltip = (WrapperComponent: any) => {
    return class WithAuth<P> extends React.Component<P & withTooltipProps> {
        render() {
            const { tooltip, ...rest } = this.props;

            const pos = tooltip?.placement ? tooltip.placement : 'auto';
            const tType = tooltip?.triggerType;

            if (tooltip?.content) {
                return (
                    <LayerTooltip
                        placement={pos}
                        content={tooltip.content}
                        triggerType={tType}
                    >
                        <span /* {...anchorProps} */>
                            <WrapperComponent {...rest} tabIndex={0} />
                        </span>
                    </LayerTooltip>
                );
            }

            return <WrapperComponent {...rest} />;
        }
    };
};
